import { Box, Typography } from "@mui/material";
import dataProvider from "misc/dataProvider";
import { defaultListFetchParams, fetchEventBaseOrParent, fetchLocations } from "misc/edmClient";
import { manageEmailingSenders } from "misc/permissionHelpers";
import { sortArray } from "misc/typedDataHelpers";
import { useEffect, useState } from "react";
import { LinearProgress, useTranslate } from "react-admin";

import { EventBaseRecordType } from "../../pages/EventBase/EventBase.types";
import { EventLocationRecordType } from "../../pages/EventLocation/EventLocation.types";
import { ExternalEmailTemplateRecordType } from "../../pages/ExternalEmailTemplate/ExternalEmailTemplate.types";
import { EmailSenderRecordType, FileRecordType } from "../../pages/SharedRecords.types";
import ComposeEmailForm from "./ComposeEmailForm";
import { SendEmailDialogContentPropsType } from "./SendEmailDialog.types";

export const TEMPLATE_TYPES = {
  ATTENDEE: "ATTENDEE",
  ATTENDANCE_CONFIRMATION: "ATTENDANCE_CONFIRMATION",
  LECTURER: "LECTURER",
  INVOICE: "INVOICE",
  BOOKING_CONFIRMATION: "BOOKING_CONFIRMATION",
  FEEDBACK: "FEEDBACK",
  WAITING_LIST: "WAITING_LIST",
  BOOKING: "BOOKING",
  LECTURER_BOOKING: "LECTURER_BOOKING",
  ORGANIZATION: "ORGANIZATION",
};

export default function SendEmailDialogContent(props: SendEmailDialogContentPropsType) {
  const {
    event,
    recipients,
    eventBase = null,
    templateType,
    closeDialog,
    withGeneratedDocument = false,
    onBackClick,
    onSuccess,
    files = [],
  } = props;
  const [locations, setLocations] = useState<EventLocationRecordType[]>([]);
  const [fetchedEventBase, setEventBase] = useState<EventBaseRecordType | null>(eventBase);
  const [templates, setTemplates] = useState<ExternalEmailTemplateRecordType[] | false>(false);
  const [templatesLoaded, setTemplatesLoaded] = useState<boolean>(false);
  const [assignedSenders, setAssignedSenders] = useState<EmailSenderRecordType[] | false>(false);
  const [sendersLoaded, setSendersLoaded] = useState<boolean>(false);
  const translate = useTranslate();

  useEffect(() => {
    function setup() {
      if (event) {
        const locationIds = event.dates
          .reduce((acc, { location }) => acc.concat(location), [event.location])
          .filter((id) => !!id);

        if (locationIds) {
          fetchLocations({ id: locationIds }).then(setLocations);
        }

        fetchEventBaseOrParent(event.event_base).then(setEventBase);
      }

      if (manageEmailingSenders()) {
        const params = {
          ...defaultListFetchParams,
          filter: { assigned_only: true },
        };

        dataProvider.getList<EmailSenderRecordType>("email_senders", params).then((res) => {
          setAssignedSenders(res.data || []);
          setSendersLoaded(true);
        });
      }

      if (!templates) {
        const params = {
          ...defaultListFetchParams,
          filter: { scope: `${templateType || "ATTENDEE"}_TEMPLATES`, parent_template__isnull: true },
        };

        dataProvider.getList<ExternalEmailTemplateRecordType>("external_email_templates", params).then((res) => {
          setTemplatesLoaded(true);
          setTemplates(res.data || []);
        });
      }
    }

    setup();
  }, [event, templates, templateType]);

  let combinedFiles = files;

  if (fetchedEventBase) {
    combinedFiles = files.concat(fetchedEventBase.documents);
  }

  if (event) {
    const locationFiles = locations.reduce<FileRecordType[]>((acc, { documents }) => acc.concat(documents), []);
    combinedFiles = files.concat(locationFiles).concat(event.files);
  }

  if (!templates || (event && !files)) return <LinearProgress timeout={0} />;

  if (templatesLoaded && templates.length === 0) {
    return (
      <Box>
        <Typography>{translate("emailing.no_templates_for_scope")}</Typography>
      </Box>
    );
  }

  if (manageEmailingSenders() && sendersLoaded && assignedSenders && assignedSenders.length === 0) {
    return (
      <Box>
        <Typography>{translate("emailing.no_assigned_senders")}</Typography>
      </Box>
    );
  }

  return (
    <ComposeEmailForm
      availableFiles={sortArray(combinedFiles, "desc", "original_filename")}
      closeDialog={closeDialog}
      templates={templates}
      withGeneratedDocument={withGeneratedDocument}
      onBackClick={onBackClick}
      event={event}
      onSuccess={onSuccess}
      recipients={recipients}
    />
  );
}
